import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { ENV_CONFIG } from '@sc/data-access-environment';
import {
  AppointmentsGroupBy,
  Dossier,
  Environment,
  FilterDisplayStyle,
  SearchFilters,
  TimeFilters,
} from '@sc/data-access-models';
import { DossierSandboxService } from '@sc/data-access-state-management';
import { StorageService } from '@sc/data-access-storage';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { filter, switchMap, take } from 'rxjs/operators';
import { CheckboxFilterComponent } from '../checkbox-filter';
import { IconComponent } from '../icon';
import { SelectComponent } from '../select';
import { ToggleComponent } from '../toggle';

@Component({
  selector: 'sc-appointment-filters',
  styleUrls: ['./appointment-filters.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    IconComponent,
    ToggleComponent,
    SelectComponent,
    MatMenuModule,
    CheckboxFilterComponent,
    MatCardModule,
    MatRadioModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  template: `
    <div class="container">
      <div class="toggle-and-top-filters" tourAnchor="intervention-filters">
        <!-- Map/List toggle -->
        <sc-toggle
          *ngIf="displayListMapToggle"
          class="list-map-toggle"
          [offLabel]="
            'CustomerDeclaration.AppointmentSearchPage.toggleList' | translate
          "
          [onLabel]="
            'CustomerDeclaration.AppointmentSearchPage.toggleMap' | translate
          "
          [value]="false"
          (valueChange)="onToggleChange($event)"
        ></sc-toggle>

        <!-- B2B style of GroupBy filter -->
        <div
          *ngIf="displayWithBackgroundStyle() && displayGroupBy"
          class="filters-summary-item group-by-filters-container"
          tabindex="0"
          role="button"
          [matMenuTriggerFor]="groupByFiltersMenu"
          #groupByFiltersMenuTrigger
          (keydown.enter)="toggleGroupByFiltersMenu()"
          (keydown.space)="toggleGroupByFiltersMenu()"
        >
          <div class="filters-btn-display">
            <span class="label">
              {{ 'AppointmentCommonList.groupByFilterTitle' | translate }}
            </span>
            <span class="value">
              {{ getGroupByTextKey(searchFilters?.groupBy) | translate }}
            </span>
          </div>
        </div>
        <mat-menu #groupByFiltersMenu="matMenu" class="filters-options-card">
          <mat-card appearance="outlined" class="group-by-list checkbox-list">
            <mat-radio-group
              #radioGroupByFilters
              [value]="searchFilters?.groupBy"
              (change)="onGroupByChange($event?.value)"
            >
              <mat-radio-button
                id="appointmentViewNearest"
                value="days"
                matMenuItem
                role="menuitemradio"
              >
                {{ getGroupByTextKey('days') | translate }}
              </mat-radio-button>
              <mat-radio-button
                id="appointmentViewEarliest"
                value="fitters"
                matMenuItem
                role="menuitemradio"
              >
                {{ getGroupByTextKey('fitters') | translate }}
              </mat-radio-button>
            </mat-radio-group>
          </mat-card>
        </mat-menu>

        <!-- B2C style of GroupBy filter -->
        <sc-select
          *ngIf="!displayWithBackgroundStyle() && displayGroupBy"
          class="group-by-options-btn"
          [value]="searchFilters?.groupBy"
          [options]="
            featureFlippingService.isFeatureActivated(
              'display-reverse-appointment-filter'
            )
              ? ['fitters', 'days']
              : ['days', 'fitters']
          "
          panelClass="group-by-options-pannel"
          (valueChange)="onGroupByChange($event)"
        >
          <ng-template #triggerTemplate let-item>
            {{ getGroupByTextKey(searchFilters?.groupBy) | translate }}
          </ng-template>

          <ng-template #optionTemplate let-item>
            {{ getGroupByTextKey(item) | translate }}
          </ng-template>
        </sc-select>

        <!-- B2B style of Time filters -->
        <div
          class="filters-summary-item time-filters-container"
          *ngIf="displayWithBackgroundStyle() && displayTimeFilters"
          tabindex="0"
          role="button"
          [matMenuTriggerFor]="timeFiltersMenu"
          #timeFiltersMenuTrigger
          (keydown.enter)="toggleTimeFiltersMenu()"
          (keydown.space)="toggleTimeFiltersMenu()"
        >
          <div class="filters-btn-display">
            <span class="label">
              {{ 'AppointmentCommonList.timeFilterTitle' | translate }} :
            </span>
            <span class="value">
              {{ getTimeFilterDisplay(searchFilters?.period) }}
            </span>
          </div>
        </div>
        <mat-menu #timeFiltersMenu="matMenu" class="filters-options-card">
          <mat-card appearance="outlined" class="time-list checkbox-list">
            <mat-radio-group
              #radioTimeFilters
              [value]="searchFilters?.period"
              (change)="onTimeFiltersChange($event?.value)"
            >
              <ng-container
                *ngFor="let time of configurationTimeFilters; let index = index"
              >
                <mat-radio-button
                  [value]="time"
                  matMenuItem
                  role="menuitemradio"
                >
                  {{ getTimeFilterDisplay(time) }}
                </mat-radio-button>
              </ng-container>
            </mat-radio-group>
          </mat-card>
        </mat-menu>

        <!-- NOTE - put B2C style of Time filter here -->
      </div>
      <div class="fitter-type-filters" *ngIf="_displayGeneralistsFilter">
        <sc-checkbox-filter
          [text]="
            'AppointmentCommonList.displaySpecialistFilterTitle' | translate
          "
          [toggleValue]="
            searchFilters?.interventionParams?.displayGlassSpecialist
          "
          type="specialist"
          [aria-label]="
            'GlobalApplicativeConstants.accessibility.labels.displaySpecialistFilterTitle'
              | translate
          "
          (toggleEvent)="onChangeGlassSpecialist($event)"
        ></sc-checkbox-filter>
        <sc-checkbox-filter
          *ngIf="
            _displayGeneralistsFilter && searchFilters?.groupBy === 'fitters'
          "
          [text]="
            'AppointmentCommonList.displayGeneralistsFilterTitle' | translate
          "
          [toggleValue]="searchFilters?.interventionParams?.displayGeneralists"
          type="generalist"
          [aria-label]="
            'GlobalApplicativeConstants.accessibility.labels.displayGeneralistsFilterTitle'
              | translate
          "
          (toggleEvent)="onChangeGeneralists($event)"
        ></sc-checkbox-filter>
      </div>
      <div
        class="appointment-type-filters"
        *ngIf="
          displayFitterInterventionFilters || displayOutsideInterventionFilters
        "
      >
        <sc-checkbox-filter
          *ngIf="displayFitterInterventionFilters"
          [text]="
            'AppointmentCommonList.fitterInterventionFilterTitle' | translate
          "
          [toggleValue]="searchFilters?.interventionParams?.fitterIntervention"
          icon="fitter"
          specificIcon="search_fitter"
          type="garage"
          [aria-label]="
            'GlobalApplicativeConstants.accessibility.labels.inCenterSwitch'
              | translate
          "
          [disabled]="
            !searchFilters?.interventionParams?.displayGlassSpecialist ||
            isMobilityDisabled()
          "
          (toggleEvent)="onChangeFitterIntervention($event)"
        ></sc-checkbox-filter>
        <sc-checkbox-filter
          *ngIf="
            displayOutsideInterventionFilters &&
            !searchFilters?.interventionParams?.replacementVehicle &&
            !isMobilityDisabled()
          "
          [text]="
            'AppointmentCommonList.outsideInterventionFilterTitle' | translate
          "
          [toggleValue]="searchFilters?.interventionParams?.outsideIntervention"
          icon="home"
          specificIcon="search_home"
          type="outside-pad"
          [aria-label]="
            'GlobalApplicativeConstants.accessibility.labels.outsideSwitch'
              | translate
          "
          [disabled]="
            !searchFilters?.interventionParams?.displayGlassSpecialist
          "
          (toggleEvent)="onChangeOutsideIntervention($event)"
        ></sc-checkbox-filter>
      </div>
      <div class="toggle-filters-container">
        <div
          class="replacement-vehicle-filter"
          *ngIf="_displayReplacementVehicleFilters"
        >
          <div
            class="filters-summary-rv"
            [ngClass]="{
              disabled: isReplacementVechicleDisabled()
            }"
          >
            <mat-slide-toggle
              [checked]="searchFilters?.interventionParams?.replacementVehicle"
              [disableRipple]="true"
              [disabled]="isReplacementVechicleDisabled()"
              [hideIcon]="true"
              [attr.data-testid]="'replacementVehicleToggle'"
              labelPosition="after"
              aria-labelledby="rv-toggle-label"
              (change)="onChangeReplacementVehicle($event?.checked)"
            >
              <sc-icon
                aria-hidden="true"
                name="car"
                specificName="replacement_vehicle"
                [forceInitialColor]="true"
              ></sc-icon>
              <span
                id="rv-toggle-label"
                class="filter-rv-text sc-design-text-4"
                aria-hidden="true"
                >{{
                  'AppointmentCommonList.onlyReplacementVehicleFitlerTitle'
                    | translate
                }}</span
              >
            </mat-slide-toggle>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class AppointmentFiltersComponent implements OnInit, AfterViewInit {
  @ViewChild('groupByFiltersMenuTrigger', { read: MatMenuTrigger })
  groupByFiltersMenuTrigger: MatMenuTrigger;
  @ViewChild('timeFiltersMenuTrigger', { read: MatMenuTrigger })
  timeFiltersMenuTrigger: MatMenuTrigger;

  @ViewChild('radioGroupByFilters', { static: false, read: ElementRef })
  radioGroupByFilters: ElementRef<HTMLElement>;
  @ViewChild('radioTimeFilters', { static: false, read: ElementRef })
  radioTimeFilters: ElementRef<HTMLElement>;

  @Input() displayListMapToggle = false;
  @Input() displayStyle: FilterDisplayStyle = 'with-background';
  @Input() displayTimeFilters = true;
  @Input() displayGroupBy = true;
  @Input() displayFitterInterventionFilters = true;
  @Input() displayOutsideInterventionFilters = true;

  _displayReplacementVehicleFilters = true;
  @Input() set displayReplacementVehicleFilters(value: boolean) {
    this._displayReplacementVehicleFilters = value;
    if (!value)
      this.searchFilters.interventionParams.replacementVehicle = false;
  }
  _displayGeneralistsFilter = false;
  @Input() set displayGeneralistsFilter(value: boolean) {
    this._displayGeneralistsFilter = value;
    if (!value)
      this.searchFilters.interventionParams.displayGeneralists = false;
  }
  _displayGlassSpecialistFilter = false;
  @Input() set displayGlassSpecialistFilter(value: boolean) {
    this._displayGlassSpecialistFilter = value;
    if (!value)
      this.searchFilters.interventionParams.displayGlassSpecialist = true;
  }
  @Input() defaultListMapToggle = true;
  @Input() configurationTimeFilters: (TimeFilters | null)[] = [];

  @Output() changeListMapToggle = new EventEmitter<boolean>();

  searchFilters: SearchFilters;

  defaultFilters: SearchFilters = {
    groupBy: this.featureFlippingService.isFeatureActivated(
      'search-page-default-sort-by-nearest'
    )
      ? 'fitters'
      : 'days',
    period: null,
    interventionParams: {
      fitterIntervention: true,
      outsideIntervention: !this.featureFlippingService.isFeatureActivated(
        'search-page-default-uncheck-outsideIntervention'
      ),
      replacementVehicle: false,
      displayGeneralists: false,
      displayGlassSpecialist: true,
    },
  };

  storagePlace: Storage;

  currentDossier: Dossier;

  constructor(
    private readonly localeDatePipe: LocaleDatePipe,
    private readonly translatorService: TranslatorService,
    public featureFlippingService: FeatureFlippingManagerService,
    private readonly dossierSB: DossierSandboxService,
    private readonly storage: StorageService,
    @Inject(ENV_CONFIG) protected env: Environment
  ) {
    this.storagePlace =
      env?.auth?.tokenManager?.storage === 'sessionStorage'
        ? sessionStorage
        : localStorage;

    this.searchFilters = this.defaultFilters;
  }
  ngOnInit(): void {
    this.dossierSB.dossier$
      .pipe(
        filter((dossier) => !!dossier),
        take(1),
        switchMap((dossier) => {
          const groupBy = this.storage.getItem(
            'search-filter-group-by',
            this.storagePlace,
            dossier?.searchCriteria?.filters?.groupBy ??
              this.defaultFilters?.groupBy
          ) as AppointmentsGroupBy;

          return this.dossierSB.patchDossierSearchFilters({
            ...this.defaultFilters,
            ...dossier?.searchCriteria?.filters,
            groupBy: groupBy,
            interventionParams: {
              ...this.defaultFilters.interventionParams,
              ...dossier?.searchCriteria?.filters?.interventionParams,
              fitterIntervention: this.isMobilityDisabled()
                ? true
                : dossier?.searchCriteria?.filters?.interventionParams
                    ?.fitterIntervention ??
                  this.defaultFilters?.interventionParams?.fitterIntervention,
              outsideIntervention: this.isMobilityDisabled()
                ? false
                : dossier?.searchCriteria?.filters?.interventionParams
                    ?.outsideIntervention ??
                  this.defaultFilters?.interventionParams?.outsideIntervention,
            },
          });
        })
      )
      .subscribe();
    this.dossierSB.dossier$.subscribe((dossier) => {
      this.searchFilters = {
        ...dossier?.searchCriteria?.filters,
        groupBy:
          dossier?.searchCriteria?.filters?.groupBy ||
          this.defaultFilters.groupBy,
        period: dossier?.searchCriteria?.filters?.period
          ? { ...dossier?.searchCriteria?.filters?.period }
          : null,
        interventionParams: {
          ...this.defaultFilters.interventionParams,
          ...dossier?.searchCriteria?.filters?.interventionParams,
          fitterIntervention: this.isMobilityDisabled()
            ? true
            : dossier?.searchCriteria?.filters?.interventionParams
                ?.fitterIntervention ??
              this.defaultFilters?.interventionParams?.fitterIntervention,
          outsideIntervention: this.isMobilityDisabled()
            ? false
            : dossier?.searchCriteria?.filters?.interventionParams
                ?.outsideIntervention ??
              this.defaultFilters?.interventionParams?.outsideIntervention,
        },
      };
      this.currentDossier = dossier;
    });
  }

  ngAfterViewInit(): void {
    this.groupByFiltersMenuTrigger?.menuOpened
      ?.asObservable()
      ?.subscribe(() => {
        (
          this.radioGroupByFilters?.nativeElement.querySelector(
            '.mat-radio-checked [tabindex]'
          ) as HTMLElement
        )?.focus();
      });

    this.timeFiltersMenuTrigger?.menuOpened?.asObservable()?.subscribe(() => {
      (
        this.radioTimeFilters?.nativeElement.querySelector(
          '.mat-radio-checked [tabindex]'
        ) as HTMLElement
      )?.focus();
    });
  }

  getTimeFilterDisplay(timeFilter: TimeFilters | null) {
    if (!timeFilter || typeof timeFilter === 'string') {
      return this.translatorService.getInstantTranslation(
        'AppointmentCommonList.timeFilterValueAll'
      );
    }

    const beginTime = new Date().setHours(timeFilter.start, 0, 0, 0);
    const endTime = new Date().setHours(timeFilter.end, 0, 0, 0);

    const formattedBeginTime = this.localeDatePipe.getInstantTransform(
      beginTime,
      'shortTime'
    );
    const formattedEndTime = this.localeDatePipe.getInstantTransform(
      endTime,
      'shortTime'
    );

    return formattedBeginTime + ' - ' + formattedEndTime;
  }

  getGroupByTextKey(groupByValue: AppointmentsGroupBy) {
    return groupByValue === 'days'
      ? 'AppointmentCommonList.sortByDate'
      : 'AppointmentCommonList.sortByDistance';
  }

  displayWithBackgroundStyle() {
    return this.displayStyle === 'with-background';
  }

  isReplacementVechicleDisabled() {
    return (
      (this.searchFilters?.interventionParams?.outsideIntervention &&
        !this.searchFilters?.interventionParams?.fitterIntervention) ||
      this.searchFilters?.interventionParams?.displayGeneralists
    );
  }

  toggleGroupByFiltersMenu() {
    this.groupByFiltersMenuTrigger?.toggleMenu();
  }

  toggleTimeFiltersMenu() {
    this.timeFiltersMenuTrigger?.toggleMenu();
  }

  onTimeFiltersChange(timeFilter: TimeFilters | null) {
    this.searchFilters.period = timeFilter;
    this.dossierSB
      .patchDossierSearchFilters({
        period: timeFilter,
      })
      .subscribe();
  }

  onGroupByChange(groupByValue: AppointmentsGroupBy) {
    this.searchFilters.groupBy = groupByValue;
    this.dossierSB
      .patchDossierSearchFilters({
        groupBy: groupByValue,
      })
      .subscribe();
    this.storage.setItem(
      'search-filter-group-by',
      this.searchFilters?.groupBy,
      this.storagePlace
    );
  }

  onToggleChange(toggleValue: boolean) {
    this.changeListMapToggle.emit(!toggleValue);
  }

  onChangeFitterIntervention(newValue: boolean) {
    this.searchFilters.interventionParams.fitterIntervention = newValue;
    this.dossierSB
      .patchDossierSearchFilters({
        interventionParams: { fitterIntervention: newValue },
      })
      .subscribe();
  }

  onChangeOutsideIntervention(newValue: boolean) {
    this.searchFilters.interventionParams.outsideIntervention = newValue;
    this.dossierSB
      .patchDossierSearchFilters({
        interventionParams: { outsideIntervention: newValue },
      })
      .subscribe();
  }

  onChangeReplacementVehicle(newValue: boolean) {
    this.searchFilters.interventionParams.replacementVehicle = newValue;
    this.dossierSB
      .patchDossierSearchFilters({
        interventionParams: { replacementVehicle: newValue },
      })
      .subscribe();
  }

  onChangeGeneralists(newValue: boolean) {
    this.searchFilters.interventionParams.displayGeneralists = newValue;
    this.dossierSB
      .patchDossierSearchFilters({
        interventionParams: {
          replacementVehicle:
            newValue === true
              ? !newValue
              : this.searchFilters.interventionParams.replacementVehicle,
          displayGeneralists: newValue,
          displayGlassSpecialist:
            newValue === true
              ? !newValue
              : this.searchFilters.interventionParams.displayGlassSpecialist,
        },
      })
      .subscribe();
  }

  onChangeGlassSpecialist(newValue: boolean) {
    this.searchFilters.interventionParams.displayGlassSpecialist = newValue;
    this.dossierSB
      .patchDossierSearchFilters({
        interventionParams: {
          displayGlassSpecialist: newValue,
          displayGeneralists:
            newValue === true
              ? !newValue
              : this.searchFilters.interventionParams.displayGeneralists,
        },
      })
      .subscribe();
  }

  // TODO: 7590
  isMobilityDisabled(): boolean {
    return (
      this.currentDossier?.claim?.calibration === 'true' &&
      this.currentDossier?.claim?.glass?.glass === 'windshield' &&
      !this.featureFlippingService.isFeatureActivated('mobility-with-adas')
    );
  }
}
